import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { AuthService } from "../utils/auth";
import { useToast } from "../contexts/ToastContext";
import { Loader2, Eye, EyeOff } from "lucide-react";
import AuthShell, { authField, authLabel } from "../v2/pages/AuthShell";
import { Arrow } from "../v2/components/Primitives";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const Rule: React.FC<{ ok: boolean; children: React.ReactNode }> = ({ ok, children }) => (
  <li className={`flex items-center gap-2 ${ok ? "text-teal-deep" : "text-ink/45"}`}>
    <span className={`h-px w-3 ${ok ? "bg-teal-deep" : "bg-ink/30"}`} />
    {children}
  </li>
);

const VerifyEmailOtp: React.FC = () => {
  const query = useQuery();
  const params = useParams<{ lang?: string }>();
  const currentLanguage = params.lang || "fr";
  const isFr = currentLanguage === "fr";
  const emailParam = query.get("email") || "";
  const expiresAtParam = query.get("expiresAt");
  const flow = (query.get("flow") || "verify").toLowerCase(); // "verify" | "reset"

  const { login } = useAuth();
  const { success: toastSuccess, error: toastError, info: toastInfo } = useToast();
  const navigate = useNavigate();

  const [email] = useState<string>(emailParam);
  const [code, setCode] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(60);
  const infoToastShownRef = useRef<boolean>(false);
  const [step, setStep] = useState<"verify" | "reset">("verify");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [passwordFocused, setPasswordFocused] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  // Password live checks (mirror Signup.tsx)
  const npwd = newPassword || "";
  const hasMin = npwd.length >= 8;
  const hasUpper = /[A-Z]/.test(npwd);
  const hasLower = /[a-z]/.test(npwd);
  const hasSpecial = /[^A-Za-z0-9]/.test(npwd);
  const hasNumber = /[0-9]/.test(npwd);
  const confirmMatches = (confirmPassword?.length || 0) > 0 && confirmPassword === newPassword;

  // Show info toast only once (when arriving). Message varies by flow
  useEffect(() => {
    if (!infoToastShownRef.current) {
      infoToastShownRef.current = true;
      const msgFr =
        flow === "reset"
          ? "Veuillez saisir le code OTP envoyé pour réinitialiser votre mot de passe."
          : "Veuillez saisir le code OTP envoyé. Il expire bientôt.";
      const msgEn =
        flow === "reset"
          ? "Please enter the OTP code sent to reset your password."
          : "Please enter the OTP code sent to you. It will expire soon.";
      toastInfo(isFr ? msgFr : msgEn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAtParam, currentLanguage, flow]);

  // Start cooldown timer once on mount
  useEffect(() => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toastError(isFr ? "Email manquant. Revenez à l'inscription." : "Missing email. Please go back to signup.");
      navigate(`/${currentLanguage}/login`);
      return;
    }
    if (code.length !== 6) {
      toastError(isFr ? "Le code doit contenir 6 chiffres" : "The code must be 6 digits");
      return;
    }

    setIsSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";
      if (flow === "reset") {
        setStep("reset");
        toastSuccess(
          isFr ? "Code vérifié. Vous pouvez définir un nouveau mot de passe." : "Code verified. You can set a new password.",
        );
      } else {
        const res = await fetch(`${apiUrl}/auth/verify-email-otp?lang=${currentLanguage}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, code }),
        });
        const data = await res.json();
        if (res.ok) {
          const payload = (data && (data.data || data)) || {};
          const tokens = payload.tokens ||
            payload.data?.tokens || { accessToken: payload.accessToken, refreshToken: payload.refreshToken };
          const user = payload.user || payload.data?.user;

          if (tokens?.accessToken && tokens?.refreshToken && user) {
            AuthService.setTokens(tokens);
            AuthService.setUserProfile(user);
            login(tokens, user);
            toastSuccess(isFr ? "Email vérifié avec succès. Bienvenue !" : "Email verified successfully. Welcome!");
            navigate(`/${currentLanguage}/dashboard`);
          } else {
            toastError(
              isFr
                ? "Vérification réussie mais connexion impossible. Veuillez vous connecter."
                : "Verification succeeded but sign-in failed. Please log in.",
            );
            navigate(`/${currentLanguage}/login`);
          }
        } else {
          toastError(data?.message || (isFr ? "Code invalide ou expiré" : "Invalid or expired code"));
        }
      }
    } catch (err) {
      toastError(isFr ? "Erreur de serveur. Réessayez." : "Server error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email || resendCooldown > 0) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";
      const url =
        flow === "reset"
          ? `${apiUrl}/auth/forgot-password?lang=${currentLanguage}`
          : `${apiUrl}/auth/resend-email-otp?lang=${currentLanguage}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setResendCooldown(60);
        toastSuccess(isFr ? "Nouveau code envoyé. Vérifiez votre email." : "New code sent. Check your email.");
      } else {
        toastError(data?.message || (isFr ? "Impossible d'envoyer le code" : "Failed to send code"));
      }
    } catch (err) {
      toastError(isFr ? "Erreur de serveur. Réessayez." : "Server error. Please try again.");
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(hasMin && hasUpper && hasLower && hasSpecial && hasNumber)) {
      toastError(
        isFr
          ? "Mot de passe invalide: minimum 8 caractères, au moins 1 majuscule, 1 minuscule, 1 chiffre et 1 caractère spécial"
          : "Invalid password: minimum 8 characters, at least 1 uppercase, 1 lowercase, 1 number and 1 special character",
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      toastError(isFr ? "Les mots de passe ne correspondent pas" : "Passwords do not match");
      return;
    }
    setIsSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";
      const res = await fetch(`${apiUrl}/auth/reset-password?lang=${currentLanguage}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: code, newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toastSuccess(
          isFr ? "Mot de passe réinitialisé avec succès. Vous pouvez vous connecter." : "Password reset successfully. You can now log in.",
        );
        navigate(`/${currentLanguage}/login`);
      } else {
        toastError(data?.message || (isFr ? "Échec de la réinitialisation du mot de passe" : "Failed to reset password"));
      }
    } catch (err) {
      toastError(isFr ? "Erreur de serveur. Réessayez." : "Server error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const title =
    flow === "reset"
      ? isFr ? "Vérification du code" : "Code verification"
      : isFr ? "Vérification de l'email" : "Email verification";

  const eye = (shown: boolean, toggle: () => void) => (
    <button
      type="button"
      onClick={toggle}
      className="absolute inset-y-0 right-0 flex items-center text-ink/40 hover:text-ink"
      aria-label={isFr ? "Afficher/masquer le mot de passe" : "Show/Hide password"}
    >
      {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  return (
    <AuthShell
      title={title}
      statement={
        isFr ? (
          <>
            Un code, six chiffres, <em className="italic text-tangerine">et c'est bon.</em>
          </>
        ) : (
          <>
            One code, six digits, <em className="italic text-tangerine">and you're in.</em>
          </>
        )
      }
      note={isFr ? "Pensez à vérifier vos courriers indésirables." : "Remember to check your spam folder."}
      description={isFr ? "Saisissez le code à 6 chiffres reçu par email." : "Enter the 6-digit code received by email."}
      img="/illustrations/landing/verify.webp"
    >
      <div className="v2-reveal">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45">
          {flow === "reset" ? (isFr ? "Mot de passe oublié" : "Forgot password") : isFr ? "Dernière étape" : "Last step"}
        </div>
        <h1 className="mt-3 font-display text-[clamp(2.2rem,4vw,3.25rem)] leading-[0.98] tracking-[-0.02em]">{title}</h1>
        <p className="mt-3 text-sm text-ink/60">
          {isFr ? "Un code de 6 chiffres a été envoyé à " : "A 6-digit code was sent to "}
          <span className="font-medium text-ink">{email || "—"}</span>.
        </p>
      </div>

      {step === "verify" && (
        <form onSubmit={handleVerify} className="v2-reveal mt-8 space-y-7" data-delay="1">
          <div>
            <label htmlFor="otp" className={authLabel}>
              {isFr ? "Code de vérification" : "Verification code"}
            </label>
            <input
              id="otp"
              type="text"
              value={code}
              onChange={handleChange}
              placeholder="000000"
              className={`${authField} font-display text-4xl tracking-[0.35em]`}
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || code.length !== 6}
            className="v2-btn v2-btn-ink w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isFr ? "Vérification…" : "Verifying…"}
              </>
            ) : (
              <>
                {isFr ? "Vérifier" : "Verify"}
                <Arrow />
              </>
            )}
          </button>

          <p className="text-sm text-ink/60">
            {`${isFr ? "Vous n'avez pas reçu le code ?" : "Didn't receive the code?"} `}
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="v2-link text-ink disabled:cursor-not-allowed disabled:opacity-50"
            >
              {resendCooldown > 0
                ? isFr ? `Renvoyer dans ${resendCooldown}s` : `Resend in ${resendCooldown}s`
                : isFr ? "Renvoyer le code" : "Resend code"}
            </button>
          </p>
        </form>
      )}

      {step === "reset" && flow === "reset" && (
        <form onSubmit={handleReset} className="v2-reveal mt-8 space-y-7" data-delay="1">
          <div>
            <label className={authLabel}>{isFr ? "Nouveau mot de passe" : "New password"}</label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                className={`${authField} pr-10`}
                placeholder={isFr ? "Minimum 8 caractères" : "Minimum 8 characters"}
                minLength={8}
                autoComplete="new-password"
                required
              />
              {eye(showNewPassword, () => setShowNewPassword((v) => !v))}
            </div>
            {(passwordFocused || newPassword.length > 0) && (
              <ul className="mt-3 space-y-1 font-mono text-[10px] uppercase tracking-[0.12em]">
                <Rule ok={hasMin}>{isFr ? "Au moins 8 caractères" : "At least 8 characters"}</Rule>
                <Rule ok={hasUpper}>{isFr ? "Au moins 1 majuscule (A-Z)" : "At least 1 uppercase (A-Z)"}</Rule>
                <Rule ok={hasLower}>{isFr ? "Au moins 1 minuscule (a-z)" : "At least 1 lowercase (a-z)"}</Rule>
                <Rule ok={hasNumber}>{isFr ? "Au moins 1 chiffre (0-9)" : "At least 1 number (0-9)"}</Rule>
                <Rule ok={hasSpecial}>{isFr ? "Au moins 1 caractère spécial" : "At least 1 special character"}</Rule>
              </ul>
            )}
          </div>
          <div>
            <label className={authLabel}>{isFr ? "Confirmer le mot de passe" : "Confirm password"}</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`${authField} pr-10`}
                autoComplete="new-password"
                required
              />
              {eye(showConfirmPassword, () => setShowConfirmPassword((v) => !v))}
            </div>
            {confirmMatches && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-teal-deep">
                {isFr ? "Les mots de passe correspondent" : "Passwords match"}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="v2-btn v2-btn-ink w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isFr ? "Réinitialisation…" : "Resetting…"}
              </>
            ) : (
              <>
                {isFr ? "Réinitialiser le mot de passe" : "Reset password"}
                <Arrow />
              </>
            )}
          </button>
        </form>
      )}

      <p className="v2-reveal mt-8 text-sm text-ink/60" data-delay="2">
        <Link to={`/${currentLanguage}/${flow === "reset" ? "login" : "signup"}`} className="v2-link text-ink">
          {isFr ? "← Retour" : "← Back"}
        </Link>
      </p>
    </AuthShell>
  );
};

export default VerifyEmailOtp;
